/**
 * Cliente do TEF ELGIN — app local instalado NO PRÓPRIO TOTEM (o GP no Windows, o
 * IDH no Android), acessado por HTTP em localhost. Quem chama é o FRONTEND do
 * totem; o backend só registra o resultado (POST /api/checkout/pedido/:id/tef).
 * Ver docs/spec-pagamento-online.md §3.4.
 *
 * Princípio de UX (totem): enviar TUDO PRONTO — crédito à vista (parcelas=1,
 * financiamento=1), valor já calculado — para o app ir direto ao "aproxime/insira
 * o cartão", evitando as telas intermediárias do ELGIN.
 *
 * Base em sischef-app/src/util/order/tefUtil.js (TEF ELGIN, a partir da linha 250).
 */

import { iniciarOperacaoCritica } from '../operacao-critica';

const ELGIN_URL =
  (import.meta.env as Record<string, string | undefined>).VITE_ELGIN_URL?.replace(/\/+$/, '') ??
  'http://localhost:2001/tef/v1';

/** Endereço em uso — o backoffice mostra para o gestor conferir na máquina do totem. */
export const URL_TEF_ELGIN = ELGIN_URL;

export type MetodoTef = 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD';

/**
 * Envelope das respostas do ELGIN. São DOIS, para o mesmo objeto `tef`:
 *
 *  - Windows (TEF GP):  `{ resultado: "<string JSON>" }`
 *  - Android (IDH):     `{ dados: { resultado: { tef: {…} } }, retorno: 0 }`
 *
 * Medido no aparelho: a documentação descreve um terceiro formato que a versão
 * em campo não usa.
 */
interface ElginEnvelope {
  resultado?: string;
  dados?: { resultado?: { tef?: ElginTef; mensagem?: string } };
}

interface ElginTef {
  /** '0' = aprovado. */
  resultadoTransacao?: string;
  mensagemResultado?: string;
  uniqueID?: string;
  nsuTransacao?: string;
  nsuTerminal?: string;
  nomeBandeira?: string;
  codigoAutorizacao?: string;
  codigoAdquirente?: string;
  tipoCartao?: string;
  dataHoraTransacao?: string;
  valorTotal?: string | number;
  comprovanteDiferenciadoPortador?: string;
  [k: string]: unknown;
}

/** Resultado normalizado, no formato que o backend (/tef) espera. */
export interface ResultadoTefNormalizado {
  metodo: MetodoTef;
  aprovado: boolean;
  mensagem: string | null;
  uniqueId?: string;
  nsu?: string;
  bandeira?: string;
  authorizationCode?: string;
  adquirente?: string;
  tipoCartao?: string;
  dataTransacao?: string;
  comprovante?: string;
  raw: unknown;
}

let configurado = false;
let chamadasEmAndamento = 0;

function avisarTefEmAndamento(): void {
  if (typeof window === 'undefined') return;

  try {
    void window.pdvDesktop?.tefEmAndamento?.(chamadasEmAndamento > 0)?.catch(() => undefined);
  } catch {
    // o aviso é conforto do kiosk; nunca pode derrubar um pagamento
  }
}

async function postElgin<T>(path: string, body: unknown): Promise<T> {
  chamadasEmAndamento += 1;
  avisarTefEmAndamento();
  const encerrarCritica = iniciarOperacaoCritica();

  try {
    const resp = await fetch(`${ELGIN_URL}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw new Error(`O terminal de pagamento respondeu ${resp.status}`);
    return (await resp.json()) as T;
  } finally {
    chamadasEmAndamento -= 1;
    avisarTefEmAndamento();
    encerrarCritica();
  }
}

/**
 * Configura o ELGIN uma vez por sessão (idempotente). É só um POST de config —
 * não abre tela no pinpad.
 */
export async function configurarElgin(): Promise<void> {
  if (configurado) return;
  await postElgin('/configuracao', dadosConfiguracao());
  configurado = true;
}

/**
 * Mesma chamada de configuração, sem a memória de "já configurei": serve para o
 * backoffice conferir se o app do TEF está no ar NESTE computador. Lança o erro
 * do fetch quando o app não responde.
 */
export async function testarConexaoElgin(): Promise<void> {
  await postElgin('/configuracao', dadosConfiguracao());
}

function dadosConfiguracao(): Record<string, string> {
  return {
    producao: import.meta.env.DEV ? '0' : '1',
    nome: 'Sischef Cardápio',
    versao: '1.0.0',
    textoPinpad: 'Sischef Cardapio',
  };
}

let ativado = false;

export async function ativarElgin(cnpj: string | null | undefined): Promise<void> {
  const documento = (cnpj ?? '').replace(/\D/g, '');
  if (ativado || !documento) return;

  await postElgin('/ativacao', { cnpjCpf: documento });
  ativado = true;
}

const TENTATIVAS_TERMINAL = 4;
const ESPERA_ENTRE_TENTATIVAS_MS = 400;

const espera = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export function ehTefAndroid(): boolean {
  return ponteAndroid() !== null;
}

function ponteAndroid(): (() => Promise<boolean>) | null {
  if (typeof window === 'undefined') return null;

  const fn = window.pdvDesktop?.iniciarTef;
  return typeof fn === 'function' ? fn.bind(window.pdvDesktop) : null;
}

export async function prepararTerminal(): Promise<void> {
  let acordou = false;

  for (let tentativa = 1; tentativa <= TENTATIVAS_TERMINAL; tentativa++) {
    try {
      await configurarElgin();
      break;
    } catch (e) {
      if (tentativa === TENTATIVAS_TERMINAL) {
        console.warn('[tef] terminal não respondeu depois de várias tentativas', e);
        return;
      }

      if (!acordou) {
        acordou = true;
        await ponteAndroid()?.().catch(() => undefined);
      }

      await espera(ESPERA_ENTRE_TENTATIVAS_MS);
    }
  }
}

/**
 * Dispara a venda no TEF para o método escolhido, com os dados prontos (à vista).
 * Síncrono do ponto de vista do fluxo: resolve quando o pinpad conclui.
 */
export async function venderTef(metodo: MetodoTef, valorCentavos: number): Promise<ResultadoTefNormalizado> {
  // Best-effort: se a config falhar, ainda tentamos a venda (o app pode já estar configurado).
  await configurarElgin().catch(() => undefined);

  const valor = String(valorCentavos)
  let env: ElginEnvelope;
  if (metodo === 'PIX') {
    env = await postElgin('/pix', { valor });
  } else if (metodo === 'DEBIT_CARD') {
    env = await postElgin('/venda/debito', { valor });
  } else {
    // Crédito à vista — parcelas/financiamento prontos evitam a tela de parcelamento.
    env = await postElgin('/venda/credito', { valor, parcelas: '1', financiamento: '1' });
  }

  const { reconhecido, tef, mensagem } = extrairTef(env);

  if (!reconhecido) throw new Error(ERRO_RESPOSTA_ILEGIVEL);

  const aprovado = tef?.resultadoTransacao === '0';

  return {
    metodo,
    aprovado,
    mensagem: tef?.mensagemResultado ?? mensagem ?? null,
    uniqueId: tef?.uniqueID,
    nsu: tef?.nsuTransacao ?? tef?.nsuTerminal,
    bandeira: tef?.nomeBandeira,
    authorizationCode: codigoAutorizacao(tef),
    adquirente: tef?.codigoAdquirente,
    tipoCartao: tef?.tipoCartao,
    dataTransacao: tef?.dataHoraTransacao,
    comprovante: tef?.comprovanteDiferenciadoPortador,
    raw: tef ?? env,
  };
}

export const ERRO_RESPOSTA_ILEGIVEL =
  'O terminal respondeu de um jeito que não conseguimos ler. NÃO repita a cobrança — chame o atendente.';

export function extrairTef(
  env: ElginEnvelope | null | undefined,
): { reconhecido: boolean; tef?: ElginTef; mensagem?: string } {
  if (!env) return { reconhecido: false };

  if (typeof env.resultado === 'string') {
    const interno = JSON.parse(env.resultado) as { tef?: ElginTef; mensagemResultado?: string };
    return { reconhecido: true, tef: interno?.tef, mensagem: interno?.mensagemResultado };
  }

  const idh = env.dados?.resultado;
  if (idh && typeof idh === 'object') return { reconhecido: true, tef: idh.tef, mensagem: idh.mensagem };

  return { reconhecido: false };
}

/** Banrisul/Banricompras usam o NSU como código de autorização (igual ao PDV). */
function codigoAutorizacao(tef?: ElginTef): string | undefined {
  const bandeira = tef?.nomeBandeira?.toUpperCase();
  if (bandeira && (bandeira === 'BANRISUL' || bandeira === 'BANRICOMPRAS')) {
    return tef?.nsuTransacao;
  }
  return tef?.codigoAutorizacao;
}
